import React from 'react';
import { SecaoComprasDrillDownContentProps } from '../../types/secaoComprasDrillDown';
import BaseSecaoDrillDownContent, { 
  BaseSecaoDrillDownData, 
  SecaoDrillDownConfig 
} from '../DrillDown/BaseSecaoDrillDownContent';

/**
 * Configuração específica para drill down de seções de compras
 * Princípio OCP: Extensível sem modificar código existente
 */
const comprasConfig: SecaoDrillDownConfig = {
  transactionType: 'compras',
  labels: {
    title: 'Resumo da Seção',
    totalTransaction: 'Total Compras',
    creditIcms: 'Crédito ICMS',
    partners: 'Fornecedores',
    products: 'Produtos',
    documents: 'Documentos',
    participation: 'Participação',
    ticketMedio: 'Ticket Médio',
    evolutionTitle: 'Evolução dos Débitos ICMS',
    topPartnersTitle: 'Top 10 Fornecedores da Seção',
    topProductsTitle: 'Top 10 Produtos da Seção',
    cfopsTitle: 'CFOPs Utilizados',
    ncmTitle: 'Análise por NCM'
  },
  colors: {
    primary: '#1976d2',
    secondary: '#42a5f5',
    success: '#2e7d32',
    warning: '#ed6c02',
    info: '#0288d1'
  },
  valueKeys: {
    transactionValue: 'total_transacao',
    partnerValue: 'valor_transacao',
    productValue: 'valor_transacao',
    evolutionValue: 'valor'
  }
};

/**
 * Adaptador para converter dados de compras para o formato base
 * Princípio SRP: Responsável apenas pela conversão de dados
 */
const adaptComprasDataToBase = (data: any): BaseSecaoDrillDownData => {
  // Verificações de segurança para evitar erros com dados undefined
  const secao = data?.secao || {};
  const evolucao_mensal = data?.evolucao_mensal || [];
  const top_fornecedores = data?.top_fornecedores || data?.top_parceiros || [];
  const principais_produtos = data?.principais_produtos || [];
  const cfops_utilizados = data?.cfops_utilizados || [];
  const analise_por_ncm = data?.analise_por_ncm || [];

  return {
    secao: {
      nome: secao.nome || 'N/A',
      total_transacao: secao.total_compras || secao.total_valor || 0,
      participacao_percentual: secao.participacao_percentual || 0,
      total_documentos: secao.total_documentos || 0,
      parceiros_distintos: secao.fornecedores_distintos || secao.parceiros_distintos || 0,
      produtos_distintos: secao.produtos_distintos || 0,
      ticket_medio: secao.ticket_medio || 0,
      credito_icms: secao.total_credito_icms || 0
    },
    evolucao_mensal: evolucao_mensal.map((item: any) => ({
      periodo: item?.periodo || '',
      valor: item?.valor || item?.compras || 0,
      documentos: item?.documentos || 0,
      parceiros: item?.fornecedores || item?.parceiros || 0,
      produtos: item?.produtos || 0
    })),
    top_parceiros: top_fornecedores.map((fornecedor: any) => ({
      nome: fornecedor?.nome || 'N/A',
      valor_transacao: fornecedor?.valor_compras ?? fornecedor?.valor_transacao ?? fornecedor?.valor_total ?? 0,
      participacao_percentual: fornecedor?.participacao_percentual ?? 0,
      documentos: fornecedor?.documentos ?? 0,
      produtos_distintos: fornecedor?.produtos_distintos ?? 0
    })),
    principais_produtos: principais_produtos.map((produto: any) => ({
      cod_item: produto?.cod_item || 'N/A',
      descricao_produto: produto?.descricao_produto || '',
      valor_transacao: produto?.valor_compras ?? produto?.valor_transacao ?? produto?.valor_total ?? 0
    })),
    cfops_utilizados: cfops_utilizados.map((cfop: any) => ({
      cfop: cfop?.cfop || 'N/A',
      descricao_cfop: cfop?.descricao_cfop || '',
      valor_total: cfop?.valor_total || 0,
      documentos: cfop?.documentos || 0,
      parceiros: cfop?.fornecedores || cfop?.parceiros || 0
    })),
    analise_por_ncm: analise_por_ncm.map((ncm: any) => ({
      ncm: ncm?.ncm || 'N/A',
      descricao_ncm: ncm?.descricao_ncm || '',
      valor_total: ncm?.valor_total || 0,
      quantidade_total: ncm?.quantidade_total || 0,
      produtos_diferentes: ncm?.produtos_diferentes || 0,
      parceiros_distintos: ncm?.fornecedores_distintos || ncm?.parceiros_distintos || 0
    }))
  };
};

/**
 * Componente para drill down de seções de compras
 * Princípio SRP: Responsável apenas pela apresentação de drill down de compras
 * Princípio OCP: Extensível via configuração
 * Princípio DIP: Depende de abstrações (componente base) não de implementações concretas
 */
const SecaoComprasDrillDownContent: React.FC<SecaoComprasDrillDownContentProps> = ({ data }) => {
  // Verificar se os dados existem antes de processar
  if (!data) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        Nenhum dado disponível para exibir
      </div>
    );
  }

  // Converter dados para o formato base
  const baseData = adaptComprasDataToBase(data);

  return (
    <BaseSecaoDrillDownContent 
      data={baseData} 
      config={comprasConfig} 
    />
  );
};

export default SecaoComprasDrillDownContent; 